import { Injectable } from '@angular/core';
import { WindowRef } from '@trademe/tangram';
import {
    fromEventPattern,
    Observable,
    race,
    ReplaySubject,
    throwError
} from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { TmWindow } from '../tm-window.interface';

@Injectable({
    providedIn: 'root'
})
export class FileReaderService {
    constructor(private _windowRef: WindowRef<TmWindow>) {}

    private static arrayBufferToString(buffer: ArrayBuffer): string {
        const array = new Uint8Array(buffer);
        return window.btoa(
            array.reduce((data, byte) => {
                return data + String.fromCharCode(byte);
            }, '')
        );
    }

    public readFileAsDataUrlAsync(file: File): Observable<string> {
        const sink = new ReplaySubject<string>(1);

        this._windowRef.useWindow((window) => {
            const fileReader = new window.FileReader();
            const addLoadHandler = (handler: (evt: Event) => any) =>
                fileReader.addEventListener('load', handler);
            const removeLoadHandler = (handler: (evt: Event) => any) =>
                fileReader.removeEventListener('load', handler);
            const addErrorHandler = (handler: (evt: ProgressEvent) => any) =>
                fileReader.addEventListener('error', handler);
            const removeErrorHandler = (handler: (evt: ProgressEvent) => any) =>
                fileReader.removeEventListener('error', handler);

            const loadEvent = fromEventPattern<Event>(
                addLoadHandler,
                removeLoadHandler
            ).pipe(
                map(() => fileReader.result),
                map((result) =>
                    result instanceof ArrayBuffer
                        ? FileReaderService.arrayBufferToString(result)
                        : result
                )
            );
            const errorEvent = fromEventPattern<ErrorEvent>(
                addErrorHandler,
                removeErrorHandler
            ).pipe(switchMap((evt) => throwError(new Error(evt.message))));

            // we have to 'capture' the winner of the two events in a replay subject before we kick of the loading
            // otherwise we would be running in a race condition:
            // the load event could be called before we started subscribing to it, so we would not notice it unless
            // we internally subscribe to the race() stream before we kick off the reading.
            // take(1) ensures that the removeLoadHandler and removeErrorHanlder functions are actually called
            // when a value or error was produced
            race(loadEvent, errorEvent).pipe(take(1)).subscribe(sink);

            // now we can kick off the loading of the file
            fileReader.readAsDataURL(file);
        });

        // we have to limit the resulting stream to just one event as we want the stream to complete immediately
        return sink.pipe(take(1));
    }
}
